import React, { useEffect, useMemo, useState } from 'react';
import { updateProfileApi } from '../../Api/BaseApi';
import { getTimeZones } from '../../utils/timezones';
import './index.css';

const ReadOnlyField = ({ label, value, placeholder }) => (
  <label className="pm-field">
    <span className="pm-field-label">{label}</span>
    <input className="pm-input" value={value || ''} placeholder={placeholder} readOnly />
  </label>
);

const EditField = ({ label, value, placeholder, onChange, list, datalistId, children }) => (
  <label className="pm-field">
    <span className="pm-field-label">{label}</span>
    <input
      className="pm-input"
      value={value || ''}
      placeholder={placeholder}
      onChange={onChange}
      list={list}
      autoComplete="off"
    />
    {datalistId && <datalist id={datalistId}>{children}</datalist>}
  </label>
);

const editableFrom = (u) => ({
  first_name: u?.first_name || '',
  last_name: u?.last_name || '',
  time_zone: u?.time_zone || '',
});

const ProfileModal = ({ isOpen, onClose, user, onLogout, onLogoutAll, onProfileUpdated }) => {
  const [form, setForm] = useState(editableFrom(user));
  const [baseline, setBaseline] = useState(editableFrom(user));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const timezones = useMemo(() => getTimeZones(), []);

  // Re-sync when a different user object is loaded (modal reopened).
  useEffect(() => {
    const e = editableFrom(user);
    setForm(e);
    setBaseline(e);
    setMsg(null);
  }, [user]);

  if (!isOpen) return null;

  const isDirty = (
    form.first_name !== baseline.first_name ||
    form.last_name !== baseline.last_name ||
    form.time_zone !== baseline.time_zone
  );

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setMsg(null);
  };

  const requestClose = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const save = async () => {
    if (!isDirty || saving) return;
    setMsg(null);
    setSaving(true);
    const changes = {};
    ['first_name', 'last_name', 'time_zone'].forEach((k) => {
      if (form[k] !== baseline[k]) changes[k] = form[k];
    });
    try {
      const res = await updateProfileApi(changes);
      const updated = res.profile || { ...user, ...changes };
      const e = editableFrom(updated);
      setForm(e);
      setBaseline(e);
      setMsg({ type: 'ok', text: res.message || 'Profile updated successfully' });
      if (onProfileUpdated) onProfileUpdated(updated);
    } catch (err) {
      const data = err.response?.data;
      const fieldErr = data?.errors && Object.values(data.errors)[0];
      setMsg({ type: 'error', text: fieldErr || data?.detail || 'Could not save your profile.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pm-backdrop" onClick={requestClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-header">
          <h3 className="pm-title">Profile</h3>
          <button className="pm-close" onClick={requestClose} aria-label="Close">✕</button>
        </div>

        <div className="pm-content">
          {user ? (
            <form className="pm-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
              <ReadOnlyField label="Email" value={user.email} placeholder="email@example.com" />

              <div className="pm-row">
                <EditField label="First Name" value={form.first_name}
                           placeholder="First name" onChange={setField('first_name')} />
                <EditField label="Last Name" value={form.last_name}
                           placeholder="Last name" onChange={setField('last_name')} />
              </div>

              <EditField label="Time Zone" value={form.time_zone}
                         placeholder="Search a time zone (e.g. Asia/Dubai)"
                         onChange={setField('time_zone')}
                         list="pm-iana-timezones" datalistId="pm-iana-timezones">
                {timezones.map((tz) => <option key={tz} value={tz} />)}
              </EditField>

              <label className="pm-field">
                <span className="pm-field-label">Role</span>
                <div className="pm-select-like" aria-readonly>
                  <span>{user.role || '—'}</span>
                </div>
              </label>

              {msg && <p className={`pm-msg ${msg.type}`}>{msg.text}</p>}

              {isDirty && (
                <button type="submit" className="pm-btn pm-save" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
            </form>
          ) : (
            <div className="pm-loading">Loading...</div>
          )}
        </div>

        <div className="pm-divider" />
        <div className="pm-footer">
          <button className="pm-btn ghost" onClick={requestClose}>Cancel</button>
          <button className="pm-btn" onClick={onLogout}>Logout</button>
          <button className="pm-btn danger" onClick={onLogoutAll}>Logout All Sessions</button>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
